/**
 * Hook to parse docling extracted text into structured Loan Estimate data
 *
 * The extracted text format has:
 * - "--- Page N ---" markers separating pages
 * - Markdown tables with | separators
 * - ## headers for sections
 * - Various formatting patterns for amounts and percentages
 */

import { useMemo } from 'react';
import type {
  ParsedLoanEstimate,
  ParseResult,
  FeeSection,
  FeeItem,
  ProjectedPaymentPeriod,
  ClosingCostsSections,
  CashToCloseSection,
  ComparisonsSection,
  LoanOfficerInfo,
  TaxesInsuranceInfo,
  OtherConsiderations
} from './types';

// ============================================
// Currency and Number Parsing Utilities
// ============================================

/**
 * Parse a currency string to number
 * Handles: $1,372,000 | $7,574.58 | -$23,286 | $0
 */
function parseCurrency(str: string | null | undefined): number {
  if (!str) {
return 0;
}
  // Remove currency symbols, commas, and handle negative
  const cleaned = str.replace(/[^0-9.-]/g, '');
  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : num;
}

/**
 * Parse a percentage string to number
 * Handles: 6.625% | 6.625 % | 147.069%
 */
function parsePercentage(str: string | null | undefined): number {
  if (!str) {
return 0;
}
  const match = str.match(/([\d.]+)\s*%/);
  if (match) {
    return parseFloat(match[1]);
  }
  return 0;
}

/**
 * Extract a value after a label from text
 */
function extractAfterLabel(text: string, label: string): string | null {
  const regex = new RegExp(`${label}[:\\s]*([^\\n|]+)`, 'i');
  const match = text.match(regex);
  return match ? match[1].trim() : null;
}

/**
 * Extract currency value after a label
 */
function extractCurrencyAfterLabel(text: string, label: string): number {
  const value = extractAfterLabel(text, label);
  return parseCurrency(value);
}

// ============================================
// Section Parsers
// ============================================

/**
 * Split text into pages by "--- Page N ---" markers
 */
function splitPages(text: string): string[] {
  const pages = text.split(/---\s*Page\s*\d+\s*---/i);
  return pages.filter(p => p.trim().length > 0);
}

/**
 * Parse the header section (lender info, date, loan ID)
 */
function parseHeader(text: string): {
  lenderName: string;
  lenderAddress: string;
  dateIssued: string;
  loanId: string;
} {
  // Lender name is usually the first ## header
  const lenderMatch = text.match(/##\s*([^#\n]+)/);
  const lenderName = lenderMatch ? lenderMatch[1].trim() : 'Unknown Lender';

  // Address is usually after the lender name
  const addressMatch = text.match(/(\d+[^#\n]+(?:St|Street|Ave|Avenue|Dr|Drive|Blvd|Boulevard)[^#\n]*)/i);
  const lenderAddress = addressMatch ? addressMatch[1].trim() : '';

  // Date issued
  const dateMatch = text.match(/DATE\s*ISSUED[:\s]*([0-9/]+)/i);
  const dateIssued = dateMatch ? dateMatch[1].trim() : '';

  // Loan ID
  const loanIdMatch = text.match(/LOAN\s*ID\s*#?[:\s]*([A-Z0-9]+)/i);
  const loanId = loanIdMatch ? loanIdMatch[1].trim() : '';

  return { lenderName, lenderAddress, dateIssued, loanId };
}

/**
 * Parse applicant and property information
 */
function parseApplicantProperty(text: string): {
  borrowerName: string;
  borrowerAddress: string;
  coborrowerName: string | null;
  propertyAddress: string;
  estimatedPropertyValue: number;
} {
  // Borrower name - appears after APPLICANTS
  const applicantsSection = text.match(/APPLICANTS?[:\s]*([^\n]+(?:\n[^\n]+)?)/i);
  let borrowerName = '';
  let borrowerAddress = '';

  if (applicantsSection) {
    const lines = applicantsSection[1].split('\n').map(l => l.trim()).filter(Boolean);
    if (lines.length > 0) {
      // First line is usually the name
      borrowerName = lines[0].replace(/PROPERTY.*$/i, '').trim();
    }
    if (lines.length > 1) {
      borrowerAddress = lines.slice(1).join(', ').replace(/PROPERTY.*$/i, '').trim();
    }
  }

  // Property address
  const propertyMatch = text.match(/PROPERTY[:\s]*([^\n]+(?:\n[^\n]+)?)/i);
  let propertyAddress = '';
  if (propertyMatch) {
    propertyAddress = propertyMatch[1].split('\n')[0]?.trim() || '';
  }

  // Estimated property value
  const propValueMatch = text.match(/EST\.?\s*PROP(?:ERTY)?\.?\s*VALUE[:\s]*\$?([\d,]+)/i);
  const estimatedPropertyValue = propValueMatch ? parseCurrency(propValueMatch[1]) : 0;

  // Coborrower - check if there's a second name
  let coborrowerName: string | null = null;
  const coMatch = text.match(/Co-?Applicant[:\s]*([^\n]+)/i);
  if (coMatch) {
    coborrowerName = coMatch[1].trim();
  }

  return {
    borrowerName,
    borrowerAddress,
    coborrowerName,
    propertyAddress,
    estimatedPropertyValue
  };
}

/**
 * Parse loan terms section
 */
function parseLoanTerms(text: string): {
  loanAmount: number;
  interestRate: number;
  monthlyPI: number;
  loanTerm: string;
  loanPurpose: string;
  loanProduct: string;
  loanType: string;
  prepaymentPenalty: boolean;
  balloonPayment: boolean;
  rateLock: { locked: boolean; until: string | null; expirationDate: string | null };
  canIncrease: {
    loanAmount: boolean;
    interestRate: boolean;
    monthlyPI: boolean;
    monthlyPIDetails: string | null;
  };
} {
  // Loan amount
  const loanAmountMatch = text.match(/Loan\s*Amount[:\s|]*\$?([\d,]+)/i);
  const loanAmount = loanAmountMatch ? parseCurrency(loanAmountMatch[1]) : 0;

  // Interest rate
  const rateMatch = text.match(/Interest\s*Rate[:\s|]*([\d.]+)\s*%/i);
  const interestRate = rateMatch ? parseFloat(rateMatch[1]) : 0;

  // Monthly P&I
  const piMatch = text.match(/Monthly\s*Principal\s*(?:&|and)\s*Interest[:\s|]*\$?([\d,.]+)/i);
  const monthlyPI = piMatch ? parseCurrency(piMatch[1]) : 0;

  // Loan term
  const termMatch = text.match(/LOAN\s*TERM[:\s]*([^\n|]+)/i);
  const loanTerm = termMatch ? termMatch[1].trim() : '';

  // Purpose
  const purposeMatch = text.match(/PURPOSE[:\s]*([^\n|]+)/i);
  const loanPurpose = purposeMatch ? purposeMatch[1].trim() : '';

  // Product
  const productMatch = text.match(/PRODUCT[:\s]*([^\n|]+)/i);
  const loanProduct = productMatch ? productMatch[1].trim() : '';

  // Loan type
  let loanType = 'Conventional';
  if (/FHA/i.test(text)) {
loanType = 'FHA';
} else if (/VA/i.test(text)) {
loanType = 'VA';
} else if (/USDA/i.test(text)) {
loanType = 'USDA';
}

  // Prepayment penalty
  const prepayMatch = text.match(/Prepayment\s*Penalty[:\s|]*(YES|NO)/i);
  const prepaymentPenalty = prepayMatch ? prepayMatch[1].toUpperCase() === 'YES' : false;

  // Balloon payment
  const balloonMatch = text.match(/Balloon\s*Payment[:\s|]*(YES|NO)/i);
  const balloonPayment = balloonMatch ? balloonMatch[1].toUpperCase() === 'YES' : false;

  // Rate lock
  const lockMatch = text.match(/RATE\s*LOCK[:\s]*(NO|YES)[,\s]*(?:until\s*)?([^\n|]*)?/i);
  const rateLock = {
    locked: lockMatch ? lockMatch[1].toUpperCase() === 'YES' : false,
    until: lockMatch && lockMatch[2] ? lockMatch[2].trim() : null,
    expirationDate: null as string | null
  };

  // Can increase flags
  const canIncrease = {
    loanAmount: false,
    interestRate: false,
    monthlyPI: false,
    monthlyPIDetails: null as string | null
  };

  // Check for "Can increase" columns
  if (/Monthly\s*P(?:rincipal)?.*increase.*YES/i.test(text)) {
    canIncrease.monthlyPI = true;
    // Try to extract details
    const detailsMatch = text.match(/Can\s*(?:go\s*)?(?:as\s*)?high\s*as\s*\$?([\d,]+)/i);
    if (detailsMatch) {
      canIncrease.monthlyPIDetails = `Can go as high as $${detailsMatch[1]}`;
    }
  }

  return {
    loanAmount,
    interestRate,
    monthlyPI,
    loanTerm,
    loanPurpose,
    loanProduct,
    loanType,
    prepaymentPenalty,
    balloonPayment,
    rateLock,
    canIncrease
  };
}

/**
 * Parse projected payments table
 */
function parseProjectedPayments(text: string): ProjectedPaymentPeriod[] {
  const payments: ProjectedPaymentPeriod[] = [];

  // Look for period labels like "Years 1-10", "Years 11-30", "Years 1-30"
  const periodMatches = text.matchAll(/Years?\s*(\d+)-(\d+)/gi);
  const periods = Array.from(periodMatches).map(m => `Years ${m[1]}-${m[2]}`);

  // If no periods found, assume single period
  if (periods.length === 0) {
    periods.push('Years 1-30');
  }

  // Extract P&I values for each period
  const piMatches = Array.from(text.matchAll(/Principal\s*(?:&|and)\s*Interest[:\s|]*\$?([\d,.]+)(?:\s*(?:only\s*interest)?)?/gi));

  // Extract MI values
  const miMatches = Array.from(text.matchAll(/Mortgage\s*Insurance[:\s|]*\+?\s*\$?([\d,.]+|0)/gi));

  // Extract Escrow values
  const escrowMatches = Array.from(text.matchAll(/Estimated\s*Escrow[:\s|]*\+?\s*\$?([\d,.]+|0)/gi));

  // Extract Total values
  const totalMatches = Array.from(text.matchAll(/Estimated\s*Total\s*Monthly\s*Payment[:\s|]*\$?([\d,.]+)/gi));

  // Check if interest only
  const isIO = /only\s*interest|interest\s*only/i.test(text);

  // Build payment periods
  for (let i = 0; i < periods.length; i++) {
    payments.push({
      label: periods[i],
      principalInterest: piMatches[i] ? parseCurrency(piMatches[i][1]) : 0,
      mortgageInsurance: miMatches[i] ? parseCurrency(miMatches[i][1]) : 0,
      escrow: escrowMatches[i] ? parseCurrency(escrowMatches[i][1]) : 0,
      totalMonthly: totalMatches[i] ? parseCurrency(totalMatches[i][1]) : 0,
      isInterestOnly: isIO && i === 0
    });
  }

  // If we only got one PI value but multiple periods, try harder
  if (payments.length < 2 && periods.length >= 2) {
    // Look for table-style data with multiple columns
    const tableRowMatch = text.match(/\|\s*\$?([\d,.]+)[^|]*\|\s*\$?([\d,.]+)/);
    if (tableRowMatch) {
      payments.length = 0;
      payments.push({
        label: periods[0] || 'Years 1-10',
        principalInterest: parseCurrency(tableRowMatch[1]),
        mortgageInsurance: 0,
        escrow: 0,
        totalMonthly: parseCurrency(tableRowMatch[1]),
        isInterestOnly: isIO
      });
      payments.push({
        label: periods[1] || 'Years 11-30',
        principalInterest: parseCurrency(tableRowMatch[2]),
        mortgageInsurance: 0,
        escrow: 0,
        totalMonthly: parseCurrency(tableRowMatch[2]),
        isInterestOnly: false
      });
    }
  }

  return payments;
}

/**
 * Parse taxes and insurance assessments
 */
function parseTaxesInsurance(text: string): TaxesInsuranceInfo {
  const monthlyMatch = text.match(/Estimated\s*Taxes[,\s]*Insurance\s*(?:&|and)\s*Assessments[:\s|]*\$?([\d,.]+)/i);
  const monthlyAmount = monthlyMatch ? parseCurrency(monthlyMatch[1]) : 0;

  return {
    monthlyAmount,
    includesPropertyTaxes: /Property\s*Taxes/i.test(text),
    includesHomeownersInsurance: /Homeowner'?s?\s*Insurance/i.test(text),
    includesOther: /Other:/i.test(text),
    otherDescription: null,
    inEscrow: {
      propertyTaxes: /Property\s*Taxes.*YES/i.test(text) || /In\s*escrow\?.*YES.*YES/i.test(text),
      homeownersInsurance: /Homeowner'?s?\s*Insurance.*YES/i.test(text) || /In\s*escrow\?.*YES/i.test(text)
    }
  };
}

/**
 * Parse closing costs sections A-J from page 2
 */
function parseClosingCosts(text: string): ClosingCostsSections {
  const sections: ClosingCostsSections = {
    sectionA: { label: 'A. Origination Charges', total: 0, items: [] },
    sectionB: { label: 'B. Services You Cannot Shop For', total: 0, items: [] },
    sectionC: { label: 'C. Services You Can Shop For', total: 0, items: [] },
    totalLoanCosts: 0,
    sectionE: { label: 'E. Taxes and Other Government Fees', total: 0, items: [] },
    sectionF: { label: 'F. Prepaids', total: 0, items: [] },
    sectionG: { label: 'G. Initial Escrow Payment at Closing', total: 0, items: [] },
    sectionH: { label: 'H. Other', total: 0, items: [] },
    totalOtherCosts: 0,
    lenderCredits: 0,
    totalClosingCosts: 0
  };

  // Section A - Origination Charges
  const sectionAMatch = text.match(/A\.\s*Origination\s*Charges[:\s|]*\$?([\d,.]+)/i);
  if (sectionAMatch) {
    sections.sectionA.total = parseCurrency(sectionAMatch[1]);
  }

  // Parse origination items
  const processingMatch = text.match(/Processing\s*Fees?[:\s|]*\$?([\d,.]+)/i);
  if (processingMatch) {
    sections.sectionA.items.push({
      description: 'Processing Fees',
      amount: parseCurrency(processingMatch[1])
    });
  }

  const uwMatch = text.match(/Underwriting\s*Fees?[:\s|]*\$?([\d,.]+)/i);
  if (uwMatch) {
    sections.sectionA.items.push({
      description: 'Underwriting Fees',
      amount: parseCurrency(uwMatch[1])
    });
  }

  const pointsMatch = text.match(/(?:Points|%\s*of\s*Loan\s*Amount)[:\s|]*\$?([\d,.]+)/i);
  if (pointsMatch && parseCurrency(pointsMatch[1]) > 0) {
    sections.sectionA.items.push({
      description: 'Points',
      amount: parseCurrency(pointsMatch[1])
    });
  }

  // Section B - Services You Cannot Shop For
  const sectionBMatch = text.match(/B\.\s*Services\s*You\s*Cannot\s*Shop\s*For[:\s|]*\$?([\d,.]+)/i);
  if (sectionBMatch) {
    sections.sectionB.total = parseCurrency(sectionBMatch[1]);
  }

  // Parse Section B items
  const appraisalMatch = text.match(/Appraisal\s*Fee[:\s|]*\$?([\d,.]+)/i);
  if (appraisalMatch) {
    sections.sectionB.items.push({
      description: 'Appraisal Fee',
      amount: parseCurrency(appraisalMatch[1])
    });
  }

  const appraisalReviewMatch = text.match(/Appraisal\s*Review\s*Fee[:\s|]*\$?([\d,.]+)/i);
  if (appraisalReviewMatch) {
    sections.sectionB.items.push({
      description: 'Appraisal Review Fee',
      amount: parseCurrency(appraisalReviewMatch[1])
    });
  }

  const creditMatch = text.match(/Credit\s*Report[:\s|]*\$?([\d,.]+)/i);
  if (creditMatch) {
    sections.sectionB.items.push({
      description: 'Credit Report',
      amount: parseCurrency(creditMatch[1])
    });
  }

  const trustMatch = text.match(/Trust\s*Review\s*Fee[:\s|]*\$?([\d,.]+)/i);
  if (trustMatch) {
    sections.sectionB.items.push({
      description: 'Trust Review Fee',
      amount: parseCurrency(trustMatch[1])
    });
  }

  // Section C - Services You Can Shop For
  const sectionCMatch = text.match(/C\.\s*Services\s*You\s*Can\s*Shop\s*For[:\s|]*\$?([\d,.]+)/i);
  if (sectionCMatch) {
    sections.sectionC.total = parseCurrency(sectionCMatch[1]);
  }

  // Parse title fees
  const titleFees = [
    { pattern: /Title-?\s*Admin\s*Fee[:\s|]*\$?([\d,.]+)/i, name: 'Title - Admin Fee' },
    { pattern: /Title-?\s*Downloading[^|]*[:\s|]*\$?([\d,.]+)/i, name: 'Title - Downloading of Loan Documents' },
    { pattern: /Title-?\s*Escrow\s*Fee[:\s|]*\$?([\d,.]+)/i, name: 'Title - Escrow Fee' },
    { pattern: /Title-?\s*Lender'?s?\s*Title\s*(?:Insurance|Policy)[:\s|]*\$?([\d,.]+)/i, name: "Title - Lender's Title Insurance" },
    { pattern: /Title-?\s*Notary\s*Fee[:\s|]*\$?([\d,.]+)/i, name: 'Title - Notary Fee' },
    { pattern: /Title-?\s*Wire\s*Transfer\s*Fee[:\s|]*\$?([\d,.]+)/i, name: 'Title - Wire Transfer Fee' },
    { pattern: /Title-?\s*Overnight\s*Fee[:\s|]*\$?([\d,.]+)/i, name: 'Title - Overnight Fee' },
    { pattern: /Title-?\s*Payoff\s*Fee[:\s|]*\$?([\d,.]+)/i, name: 'Title - Payoff Fee' },
    { pattern: /Title-?\s*Recording\s*Service\s*Fee[:\s|]*\$?([\d,.]+)/i, name: 'Title - Recording Service Fee' },
    { pattern: /Title-?\s*Sub\s*Escrow\s*Fee[:\s|]*\$?([\d,.]+)/i, name: 'Title - Sub Escrow Fee' },
    { pattern: /Title-?\s*Title\s*Endorsement\s*Fee[:\s|]*\$?([\d,.]+)/i, name: 'Title - Endorsement Fee' },
    { pattern: /Title-?\s*Courier\s*Fee[:\s|]*\$?([\d,.]+)/i, name: 'Title - Courier Fee' }
  ];

  for (const fee of titleFees) {
    const match = text.match(fee.pattern);
    if (match) {
      sections.sectionC.items.push({
        description: fee.name,
        amount: parseCurrency(match[1])
      });
    }
  }

  // Section D - Total Loan Costs
  const totalLoanMatch = text.match(/D\.?\s*TOTAL\s*LOAN\s*COSTS[:\s|]*\$?([\d,.]+)/i);
  if (totalLoanMatch) {
    sections.totalLoanCosts = parseCurrency(totalLoanMatch[1]);
  }

  // Section E - Taxes and Government Fees
  const sectionEMatch = text.match(/E\.?\s*Taxes\s*(?:and\s*)?(?:Other\s*)?Government\s*Fees[:\s|]*\$?([\d,.]+)/i);
  if (sectionEMatch) {
    sections.sectionE.total = parseCurrency(sectionEMatch[1]);
  }

  const recordingMatch = text.match(/Recording\s*Fees?(?:\s*and\s*Other\s*Taxes)?[:\s|]*\$?([\d,.]+)/i);
  if (recordingMatch) {
    sections.sectionE.items.push({
      description: 'Recording Fees',
      amount: parseCurrency(recordingMatch[1])
    });
  }

  const transferMatch = text.match(/Transfer\s*Taxes[:\s|]*\$?([\d,.]+)/i);
  if (transferMatch && parseCurrency(transferMatch[1]) > 0) {
    sections.sectionE.items.push({
      description: 'Transfer Taxes',
      amount: parseCurrency(transferMatch[1])
    });
  }

  // Section F - Prepaids
  const sectionFMatch = text.match(/F\.?\s*Prepaids[:\s|]*\$?([\d,.]+)/i);
  if (sectionFMatch) {
    sections.sectionF.total = parseCurrency(sectionFMatch[1]);
  }

  const hoiPremiumMatch = text.match(/Homeowner'?s?\s*Insurance\s*Premium[^|]*[:\s|]*\$?([\d,.]+)/i);
  if (hoiPremiumMatch) {
    sections.sectionF.items.push({
      description: "Homeowner's Insurance Premium",
      amount: parseCurrency(hoiPremiumMatch[1])
    });
  }

  const prepaidInterestMatch = text.match(/Prepaid\s*Interest[^|]*\$?([\d,.]+)(?:\s*per\s*day\s*for\s*(\d+)\s*days)?/i);
  if (prepaidInterestMatch) {
    const detail = text.match(/\$?([\d.]+)\s*per\s*day\s*for\s*(\d+)\s*days\s*@\s*([\d.]+)\s*%/i);
    sections.sectionF.items.push({
      description: 'Prepaid Interest',
      amount: parseCurrency(prepaidInterestMatch[1]),
      detail: detail ? `$${detail[1]} per day for ${detail[2]} days @ ${detail[3]}%` : undefined
    });
  }

  const propTaxPrepaidMatch = text.match(/Property\s*Taxes\s*\((\d+)\s*months?\)[:\s|]*\$?([\d,.]+)/i);
  if (propTaxPrepaidMatch) {
    sections.sectionF.items.push({
      description: `Property Taxes (${propTaxPrepaidMatch[1]} months)`,
      amount: parseCurrency(propTaxPrepaidMatch[2])
    });
  }

  // Section G - Initial Escrow
  const sectionGMatch = text.match(/G\.?\s*Initial\s*Escrow\s*Payment[^|]*[:\s|]*\$?([\d,.]+)/i);
  if (sectionGMatch) {
    sections.sectionG.total = parseCurrency(sectionGMatch[1]);
  }

  // Section H - Other
  const sectionHMatch = text.match(/H\.?\s*Other[:\s|]*\$?([\d,.]+|O)/i);
  if (sectionHMatch) {
    sections.sectionH.total = parseCurrency(sectionHMatch[1]);
  }

  // Total Other Costs
  const totalOtherMatch = text.match(/I\.?\s*TOTAL\s*OTHER\s*COSTS[:\s|]*\$?([\d,.]+)/i);
  if (totalOtherMatch) {
    sections.totalOtherCosts = parseCurrency(totalOtherMatch[1]);
  }

  // Lender Credits
  const creditsMatch = text.match(/Lender\s*Credits[:\s|]*-?\$?([\d,.]+)/i);
  if (creditsMatch) {
    sections.lenderCredits = parseCurrency(creditsMatch[1]);
  }

  // Total Closing Costs
  const totalClosingMatch = text.match(/J\.?\s*TOTAL\s*CLOSING\s*COSTS[:\s|]*\$?([\d,.]+)/i);
  if (totalClosingMatch) {
    sections.totalClosingCosts = parseCurrency(totalClosingMatch[1]);
  }

  return sections;
}

/**
 * Parse cash to close section
 */
function parseCashToClose(text: string): CashToCloseSection {
  const loanAmountMatch = text.match(/Calculating\s*Cash\s*to\s*Close[\s\S]*?Loan\s*Amount[:\s|]*\$?([\d,.]+)/i);
  const closingCostsMatch = text.match(/Total\s*Closing\s*Costs\s*\(J\)[:\s|]*-?\$?([\d,.]+)/i);
  const payoffsMatch = text.match(/Estimated\s*(?:Total\s*)?Payoffs[^|]*[:\s|]*-?\$?([\d,.]+)/i);
  const cashMatch = text.match(/Estimated\s*Cash\s*to\s*Close[^|]*[:\s|]*-?\$?([\d,.]+)/i);

  // Determine direction
  const direction = /From.*Borrower/i.test(text) ? 'from' : 'to';

  const loanAmount = loanAmountMatch ? parseCurrency(loanAmountMatch[1]) : 0;
  const totalClosingCosts = closingCostsMatch ? parseCurrency(closingCostsMatch[1]) : 0;
  const payoffs = payoffsMatch ? parseCurrency(payoffsMatch[1]) : 0;
  const cashToClose = cashMatch ? parseCurrency(cashMatch[1]) : 0;

  // PDF fallback: only basic fields available, set HUD/1003 detail fields to 0
  const isPurchase = /Purchase/i.test(text);
  return {
    purchasePrice: 0,
    loanAmount,
    estimatedClosingCosts: totalClosingCosts,
    estimatedPrepaids: 0,
    mipFundingFee: 0,
    discountPoints: 0,
    totalCosts: 0,
    sellerPaidCosts: 0,
    otherCredits: 0,
    lenderBrokerOtherPaid: 0,
    lenderPaidCC: 0,
    brokerPaidCC: 0,
    otherPaidCC: 0,
    lenderCredit: 0,
    sellerCredit: 0,
    otherAssets: 0,
    refundedMIP: 0,
    deposit: 0,
    additionalCredits: [0, 0, 0, 0],
    piggybackClosingCost: 0,
    totalLoanCredits: 0,
    totalCredits: 0,
    cashToClose: cashToClose || (loanAmount - totalClosingCosts - payoffs),
    isPurchase
  };
}

/**
 * Parse comparisons section
 */
function parseComparisons(text: string): ComparisonsSection {
  // In 5 Years - total paid
  const fiveYearTotalMatch = text.match(/In\s*5\s*Years[:\s]*\$?([\d,.]+)\s*Total/i);
  const fiveYearTotal = fiveYearTotalMatch ? parseCurrency(fiveYearTotalMatch[1]) : 0;

  // Principal paid off in 5 years
  const principalMatch = text.match(/\$?([\d,.]+)\s*Principal\s*you\s*(?:will\s*have\s*)?paid\s*off/i);
  const fiveYearPrincipal = principalMatch ? parseCurrency(principalMatch[1]) : 0;

  // APR
  const aprMatch = text.match(/Annual\s*Percentage\s*Rate\s*\(APR\)[:\s]*([\d.]+)\s*%/i);
  const apr = aprMatch ? parseFloat(aprMatch[1]) : 0;

  // TIP
  const tipMatch = text.match(/Total\s*Interest\s*Percentage\s*\(TIP\)[:\s]*([\d.]+)\s*%/i);
  const tip = tipMatch ? parseFloat(tipMatch[1]) : 0;

  return {
    fiveYearTotal,
    fiveYearPrincipal,
    apr,
    tip
  };
}

/**
 * Parse loan officer information
 */
function parseLoanOfficer(text: string): LoanOfficerInfo {
  // Look for LOAN OFFICER section
  const loSection = text.match(/LOAN\s*OFFICER[\s\S]*?(?=MORTGAGE\s*BROKER|Other\s*Considerations|Comparisons|$)/i);
  const section = loSection ? loSection[0] : text;

  // Name - usually after "LOAN OFFICER"
  const nameMatch = section.match(/LOAN\s*OFFICER[:\s]*([A-Za-z\s]+?)(?:NMLS|EMAIL|PHONE|\n)/i);
  const name = nameMatch ? nameMatch[1].trim() : '';

  // NMLS
  const nmlsMatch = section.match(/NMLS[/\s]*(?:CA\s*)?(?:LICENSE\s*)?ID[:\s]*(\d+)/i);
  const nmls = nmlsMatch ? nmlsMatch[1] : '';

  // License ID (CA License)
  const licenseMatch = section.match(/(?:CA\s*)?LICENSE\s*ID[:\s]*(\d+)/i);
  const licenseId = licenseMatch ? licenseMatch[1] : null;

  // Email
  const emailMatch = section.match(/EMAIL[:\s]*([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/i);
  const email = emailMatch ? emailMatch[1] : '';

  // Phone
  const phoneMatch = section.match(/PHONE[:\s]*([\d\-() ]+)/i);
  const phone = phoneMatch ? phoneMatch[1].trim() : '';

  return { name, nmls, licenseId, email, phone };
}

/**
 * Parse other considerations
 */
function parseOtherConsiderations(text: string): OtherConsiderations {
  const assumption = /will\s*not\s*allow\s*assumption/i.test(text) ? 'not_allowed' : 'allowed';
  const servicing = /to\s*transfer\s*servicing/i.test(text) ? 'transfer' :
                    /to\s*service\s*your\s*loan/i.test(text) ? 'service' : 'unknown';

  return {
    appraisal: 'We may order an appraisal to determine the property\'s value and charge you for this',
    assumption,
    homeownersInsurance: 'This loan requires homeowner\'s insurance on the property',
    latePayment: 'If your payment is more than 15 days late, we will charge a late fee of 5%',
    liabilityAfterForeclosure: null,
    refinance: 'Refinancing this loan will depend on your future financial situation',
    servicing
  };
}

// ============================================
// Main Parser Hook
// ============================================

export function useLoanEstimateParser(extractedText: string | null | undefined): ParseResult {
  return useMemo(() => {
    if (!extractedText || extractedText.trim().length === 0) {
      return {
        success: false,
        data: null,
        error: 'No extracted text provided',
        warnings: []
      };
    }

    const warnings: string[] = [];

    try {
      // Split into pages
      const pages = splitPages(extractedText);
      if (pages.length < 2) {
        warnings.push(`Expected at least 2 pages, found ${  pages.length}`);
      }

      const page1 = pages[0] || extractedText;
      const page2 = pages[1] || '';
      const page3 = pages[2] || '';

      // Parse each section
      const header = parseHeader(page1);
      const applicant = parseApplicantProperty(page1);
      const loanTerms = parseLoanTerms(page1);
      const projectedPayments = parseProjectedPayments(page1);
      const taxesInsurance = parseTaxesInsurance(page1);
      const closingCosts = parseClosingCosts(page2);
      const cashToClose = parseCashToClose(page2);
      const comparisons = parseComparisons(page3);
      const loanOfficer = parseLoanOfficer(page3);
      const otherConsiderations = parseOtherConsiderations(page3);

      // Extract summary closing costs from page 1
      const estClosingMatch = page1.match(/Estimated\s*Closing\s*Costs[:\s|]*\$?([\d,.]+)/i);
      const estCashMatch = page1.match(/Estimated\s*Cash\s*to\s*Close[:\s|]*\$?([\d,.]+)/i);

      const data: ParsedLoanEstimate = {
        ...header,
        ...applicant,
        ...loanTerms,
        projectedPayments,
        taxesInsuranceAssessments: taxesInsurance,
        estimatedClosingCosts: estClosingMatch ? parseCurrency(estClosingMatch[1]) : closingCosts.totalClosingCosts,
        estimatedCashToClose: estCashMatch ? parseCurrency(estCashMatch[1]) : cashToClose.cashToClose,
        closingCostsBreakdown: `$${closingCosts.totalLoanCosts.toLocaleString()} Loan Costs + $${closingCosts.totalOtherCosts.toLocaleString()} Other Costs`,
        closingCosts,
        cashToClose,
        comparisons,
        loanOfficer,
        mortgageBroker: null,
        otherConsiderations,
        rawText: extractedText
      };

      // Validation warnings
      if (data.loanAmount === 0) {
warnings.push('Could not parse loan amount');
}
      if (data.interestRate === 0) {
warnings.push('Could not parse interest rate');
}
      if (closingCosts.totalClosingCosts === 0) {
warnings.push('Could not parse total closing costs');
}

      return {
        success: true,
        data,
        error: null,
        warnings
      };
    } catch (error) {
      return {
        success: false,
        data: null,
        error: error instanceof Error ? error.message : 'Unknown parsing error',
        warnings
      };
    }
  }, [extractedText]);
}

export default useLoanEstimateParser;
